import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { fmtDate, toInputDate } from '../utils';
import { Badge, ActivityFeed, Money, TaskList, PursuitScore } from '../components/ui';
import { OppForm } from './Opportunities';
import { OpportunityStaffingTab } from './Staffing';
import { useAuth } from '../context/AuthContext';

const STAGES = ['Prospecting','Qualification','Shaping','Pre-RFP','Proposal','Evaluation','Won','Lost','No Bid','On Hold'];
const STAGE_COLORS = { Prospecting:'#8b5cf6',Qualification:'#3b82f6',Shaping:'#06b6d4','Pre-RFP':'#f59e0b',Proposal:'#f97316',Evaluation:'#ec4899',Won:'#16a34a',Lost:'#dc2626','No Bid':'#94a3b8','On Hold':'#64748b' };

const CHECKLIST_ITEMS = [
  { key:'core_capabilities', label:'Within core capabilities?' },
  { key:'customer_access', label:'Have customer access?' },
  { key:'partner_path', label:'Have partner path?' },
  { key:'vehicle_access', label:'Have vehicle access?' },
  { key:'can_staff', label:'Can we staff it?' },
  { key:'margin_acceptable', label:'Margin profile acceptable?' },
  { key:'past_performance', label:'Past performance aligned?' },
  { key:'compliance_manageable', label:'Compliance burden manageable?' },
];

export default function OpportunityDetail() {
  const { id } = useParams(); const navigate = useNavigate(); const { can } = useAuth();
  const [opp, setOpp] = useState(null); const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); const [showEdit, setShowEdit] = useState(false);
  const [users, setUsers] = useState([]);

  const load = () => {
    api.get(`/opportunities/${id}`).then(r => setOpp(r.data)).catch(() => navigate('/opportunities')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {}); }, [id]);

  const handleStageChange = async (stage) => {
    await api.put(`/opportunities/${id}`, { stage });
    load();
  };

  const handleChecklistChange = async (key, val) => {
    const updated = { ...opp.checklist, [key]: val ? 1 : 0 };
    await api.put(`/opportunities/${id}/checklist`, updated);
    load();
  };

  if (loading) return <div style={{ padding:40,color:'var(--gray-400)' }}>Loading...</div>;
  if (!opp) return null;

  const score = opp.pursuit_score || { pct: 0, score: 0, total: 8 };

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/opportunities" className="detail-back">← Opportunities</Link>
          <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
            <span className="page-title">{opp.name}</span>
            {opp.involves_ai == 1 && <span className="ai-badge">🤖 AI</span>}
            <Badge status={opp.stage} />
            <Badge status={opp.priority} />
          </div>
          <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:2 }}>
            {opp.opportunity_id} · {opp.opp_type} · {opp.category} · {opp.service_area}
            {opp.account_name && ` · ${opp.account_name}`}
          </div>
        </div>
        {can('edit') && <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>✏️ Edit</button>}
      </div>
      <div className="page-body">
        {!opp.is_active && <div className="archive-banner">📦 This opportunity is archived</div>}

        {/* Stage bar */}
        <div className="card" style={{ marginBottom:20,padding:'14px 20px' }}>
          <div style={{ fontSize:11,fontWeight:600,color:'var(--gray-500)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.4px' }}>Pipeline Stage</div>
          <div style={{ display:'flex',gap:3,flexWrap:'wrap' }}>
            {STAGES.map(s => {
              const isActive = s === opp.stage;
              return (
                <button key={s} onClick={() => can('edit') && handleStageChange(s)} style={{ flex:1,minWidth:80,padding:'7px 4px',border:'none',borderRadius:6,cursor:can('edit')?'pointer':'default',fontSize:11,fontWeight:isActive?700:400,background:isActive?STAGE_COLORS[s]:'var(--gray-100)',color:isActive?'white':'var(--gray-500)',transition:'all 0.15s' }}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex',gap:2,marginBottom:20,borderBottom:'1px solid var(--gray-200)' }}>
          {['overview','checklist','staffing','tasks','activity'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ padding:'8px 16px',background:'none',border:'none',borderBottom:tab===t?'2px solid var(--primary)':'2px solid transparent',color:tab===t?'var(--primary)':'var(--gray-500)',fontWeight:tab===t?600:400,fontSize:13,cursor:'pointer',textTransform:'capitalize',marginBottom:-1 }}>{t}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="detail-grid">
            <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
              {/* Key metrics row */}
              <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12 }}>
                {[
                  { label:'Forecasted TCV', value:<Money value={opp.forecasted_tcv} />, color:'var(--success)' },
                  { label:'Probability', value: opp.probability != null ? `${opp.probability}%` : '—', color:'var(--primary)' },
                  { label:'Close Date', value: fmtDate(opp.expected_close_date), color:'var(--gray-700)' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="card" style={{ padding:'14px 16px',textAlign:'center' }}>
                    <div style={{ fontSize:11,color:'var(--gray-500)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:18,fontWeight:700,color }}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header"><span className="card-title">Opportunity Details</span></div>
                <div className="card-body">
                  <div className="info-grid">
                    {[
                      ['Opp Type', opp.opp_type], ['Category', opp.category], ['Source', opp.source],
                      ['Service Area', opp.service_area], ['Prime', opp.prime],
                      opp.prime === 'No' && ['Teaming Partner', opp.teaming_partner],
                      opp.prime === 'No' && ['Workshare %', opp.workshare_pct ? `${opp.workshare_pct}%` : null],
                      ['Account', opp.account_name ? <span className="td-link" style={{ cursor:'pointer' }} onClick={() => navigate(`/accounts/${opp.account_id}`)}>{opp.account_name}</span> : null],
                      ['Contact', opp.contact_name || null],
                      ['Owner', opp.owner_name],
                      ['Created', fmtDate(opp.created_at)],
                    ].filter(Boolean).map(([label, val]) => (
                      <div key={label}><div className="detail-label">{label}</div><div className="detail-value">{val||'—'}</div></div>
                    ))}
                  </div>
                  {opp.description && <><hr className="divider" /><div className="detail-label">Description</div><div className="detail-value" style={{ marginTop:4,whiteSpace:'pre-wrap' }}>{opp.description}</div></>}
                  {opp.incumbent_info && <><hr className="divider" /><div className="detail-label">Incumbent Info</div><div className="detail-value" style={{ marginTop:4,whiteSpace:'pre-wrap' }}>{opp.incumbent_info}</div></>}
                  {opp.risks_blockers && <><hr className="divider" /><div className="detail-label" style={{ color:'var(--danger)' }}>Risks / Blockers</div><div className="detail-value" style={{ marginTop:4,whiteSpace:'pre-wrap' }}>{opp.risks_blockers}</div></>}
                </div>
              </div>

              {/* Pursuit Score summary */}
              <div className="card" style={{ padding:'16px 20px' }}>
                <div style={{ fontSize:12,fontWeight:600,color:'var(--gray-500)',marginBottom:12,textTransform:'uppercase',letterSpacing:'0.4px' }}>Pursuit Score</div>
                <PursuitScore checklist={opp.checklist} />
              </div>
            </div>

            <div className="card">
              <ActivityFeed relatedType="opportunity" relatedId={id} activities={opp.activities||[]} canContribute={can('contribute')} onAdd={load} />
            </div>
          </div>
        )}

        {tab === 'checklist' && (
          <div style={{ maxWidth:640 }}>
            <div className="card">
              <div className="card-header">
                <div>
                  <span className="card-title">Pursuit Checklist</span>
                  <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:2 }}>Score each criterion to calculate pursuit viability</div>
                </div>
                <PursuitScore checklist={opp.checklist} />
              </div>
              <div className="card-body">
                {CHECKLIST_ITEMS.map(item => (
                  <div key={item.key} className="checklist-row">
                    <span className="checklist-label">{item.label}</span>
                    <div style={{ display:'flex',gap:8 }}>
                      <button onClick={() => can('edit') && handleChecklistChange(item.key, true)} style={{ padding:'4px 14px',border:'none',borderRadius:5,cursor:can('edit')?'pointer':'default',fontSize:12,fontWeight:600,background:opp.checklist?.[item.key]==1?'#dcfce7':'var(--gray-100)',color:opp.checklist?.[item.key]==1?'#16a34a':'var(--gray-500)',transition:'all 0.15s' }}>Yes</button>
                      <button onClick={() => can('edit') && handleChecklistChange(item.key, false)} style={{ padding:'4px 14px',border:'none',borderRadius:5,cursor:can('edit')?'pointer':'default',fontSize:12,fontWeight:600,background:opp.checklist?.[item.key]==0&&opp.checklist?.[item.key]!==undefined?'#fee2e2':'var(--gray-100)',color:opp.checklist?.[item.key]==0&&opp.checklist?.[item.key]!==undefined?'#dc2626':'var(--gray-500)',transition:'all 0.15s' }}>No</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'staffing' && (
          <OpportunityStaffingTab opportunityId={id} opportunityName={opp.name} />
        )}

        {tab === 'tasks' && (
          <div style={{ maxWidth:640 }}>
            <div className="card">
              <TaskList relatedType="opportunity" relatedId={id} tasks={opp.tasks||[]} users={users} canContribute={can('contribute')} onUpdate={load} />
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div style={{ maxWidth:720 }}>
            <div className="card">
              <ActivityFeed relatedType="opportunity" relatedId={id} activities={opp.activities||[]} canContribute={can('contribute')} onAdd={load} />
            </div>
          </div>
        )}
      </div>
      {showEdit && <OppForm opp={opp} onSave={() => { setShowEdit(false); load(); }} onClose={() => setShowEdit(false)} />}
    </>
  );
}
